/* eslint-disable react/style-prop-object */
import {Formik} from 'formik';
import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {useHistory, useLocation, useParams} from 'react-router';
import {Button, Card, Fade} from '@material-ui/core';
import HumanReadableTime from '../../../base-components/StudioHumanReadableTime/HumanReadableTime';
import {DeployStatus} from '../DeploymentStatus/DeploymentStatus';
import {FormattedMessage, useIntl} from 'react-intl';
import {RootState, useAppDispatch} from '../../../store';
import {
  startDeployment,
  stopDeployment,
  actions as deploymentActions,
  fetchDeploymentDetails,
} from '../../../store/prodDeployments/dashboard';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from '../../../base-components/StudioToast';
import {actions as retrainActions} from '../../../store/prodDeployments/retrain';
import './DeploymentDetails.scss';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import * as Yup from 'yup';
import {
  fetchExternalStreamingDevices,
  fetchNodes,
} from '../../../store/prodDeployments/create-deployment';
import {Settings} from '@material-ui/icons';
import {StartInferenceRequest} from '../../../types/deployment/InferenceRequest';
import {DeploymentSourceInfo} from '../../../types/deployment/DeploymentSourceInfo';
import {DeploymentStatusType} from '../../../types/deployment/DeploymentResponse';

import DeploymentButton from './DeploymentButton';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../../TestDeployView/HlsVideoStream/VideoStreamSubject';
import {VideoStreamController} from '../../TestDeployView/HlsVideoStream/VideoStreamController';
import {DefaultMonitorConfig} from '../../TestDeployView/HlsVideoStream/VideoStreamMonitor';
import {VideoStream} from '../../TestDeployView/HlsVideoStream';
import {StartUponDeploymentNodeType} from '../CreateDeployment/CreateDeployment';
import ConfigureDeviceInputsTable from './ConfigureInputDevices';
import DeploymentDetailsMetadata from './DeploymentDetailsMetadata';
import {StudioTab, StudioTabPanel, StudioTabs} from '../../../base-components/StudioTab';
import Waiting from '../../../base-components/StudioWaiting/Waiting';
import InferenceAppAccordion from './InferenceAppAccordion';
import {projectFetch} from '../../../store/project';
import DeploymentDetailsSettings from './DeploymentDetailsSettings';
import DetailsViewIcon from '../../../assets/icons/DetailsViewIcon';
import SegmentationLegendView from '../../TestDeployView/SegmentationResult/SegmentationLegendView';
import {REQUEST_POLLING_INTERVAL} from '../../../config/constants';

const {setRetrainData} = retrainActions;
const {setSelectedDeployment, setDeviceGroupDeploymentDetails} = deploymentActions;

export type InputChoice = 'EXTERNAL' | 'DEVICE';
export type InputFormValues = {
  inputSourceType: InputChoice | null;
  inputSource: string | null;
  externalSourceType: InputSource | null;
};

export type DeploymentSettingsAccordionFormValues = {
  confThreshold: number;
  ddaDataMaxSize: number;
  samplingFrequency: number;
  samplingConfidence: number;
  nonMaximumSuppression: number;
};

export type SetUp = {
  srcType: boolean;
  extType: boolean;
  srcDev: boolean;
};

export const DeploymentDetails = () => {
  const [streamReady, setStreamReady] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);
  const [videoController, setVideoController] = useState<VideoStreamController>();
  const [isDeploying, setIsDeploying] = useState(false);
  const [setup, setSetup] = useState<SetUp>({
    srcType: false,
    extType: false,
    srcDev: false,
  });

  const history = useHistory();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const rDispatch = useDispatch();
  const {deploymentId} = useParams<{deploymentId: string}>();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const projectContext = params.get('projectContext');
  const origin = params.get('origin');

  const serverConfig = useSelector((state: RootState) => state.config);
  const selectedDeployment = useSelector(
    (state: RootState) => state.deploymentDashboard.selectedDeployment
  );
  const optimisticallyUpdatedStatus = useSelector(
    (state: RootState) =>
      state.deploymentDashboard.optimisticallyUpdatedStatuses[selectedDeployment?.id!]
  );
  const nodes = useSelector((state: RootState) =>
    state.createDeployment.nodes?.filter(node => node?.status === 'ONLINE')
  );
  const externalStreamingDevices = useSelector(
    (state: RootState) => state.createDeployment.externalStreamingDevices
  );

  const modelPurpose = useMemo(() => selectedDeployment?.applicationPurpose, [
    selectedDeployment?.applicationPurpose,
  ]);
  const projectId = useMemo(() => selectedDeployment?.projectId, [
    selectedDeployment?.projectId,
  ]);
  const storedDeploymentId = useMemo(() => selectedDeployment?.id, [
    selectedDeployment?.id,
  ]);
  const [selectedDeploymentSourceConfig, setSelectedDeploymentSourceConfig] = useState<
    DeploymentSourceInfo
  >();
  const [startUponDeploymentNodes, setStartUponDeploymentNodes] = useState<
    StartUponDeploymentNodeType[]
  >([]);
  const [isConfigureDeviceModalOpen, setIsConfigureDeviceModalOpen] = useState(false);
  const [expectedStatus, setExpectedStatus] = useState<DeploymentStatusType | null>();
  const [selectedTab, setSelectedTab] = useState(0);

  const status = useMemo(
    () => optimisticallyUpdatedStatus ?? selectedDeployment?.status,
    [optimisticallyUpdatedStatus, selectedDeployment?.status]
  );
  const videoOutputHlsUrl = useMemo(() => selectedDeployment?.videoOutputHlsUrl, [
    selectedDeployment?.videoOutputHlsUrl,
  ]);
  const inputSourceLocationType = useMemo(
    () => selectedDeploymentSourceConfig?.inputSourceLocationType,
    [selectedDeploymentSourceConfig?.inputSourceLocationType]
  );
  const inputSourceId = useMemo(() => selectedDeploymentSourceConfig?.inputSourceId, [
    selectedDeploymentSourceConfig?.inputSourceId,
  ]);
  const inputSourceType = useMemo(() => selectedDeploymentSourceConfig?.inputSourceType, [
    selectedDeploymentSourceConfig?.inputSourceType,
  ]);
  const streamingUrl = useMemo(() => selectedDeploymentSourceConfig?.streamingUrl, [
    selectedDeploymentSourceConfig?.streamingUrl,
  ]);

  useEffect(() => {
    if (projectId) {
      rDispatch(projectFetch(projectId));
    }
  }, [projectId, rDispatch]);

  useEffect(() => {
    if (expectedStatus === 'RUNNING' && status === 'RUNNING') {
      toast.success(`Successfully started deployment ${selectedDeployment?.name}`);
      setExpectedStatus(null);
    }

    if (expectedStatus === 'IDLE' && status === 'IDLE') {
      toast.success(`Successfully stopped deployment ${selectedDeployment?.name}`);
      setExpectedStatus(null);
    }

    if (expectedStatus === 'RUNNING' && status === 'IDLE') {
      const errorMessage = selectedDeployment?.errorMessage;
      if (errorMessage) {
        toast.error('Failed to start deployment: ' + errorMessage);
      }
    }
  }, [
    expectedStatus,
    status,
    selectedDeployment?.name,
    selectedDeployment?.errorMessage,
  ]);

  const areDeploymentSourcesValid = useCallback(() => {
    if (inputSourceLocationType === 'DEVICE' && inputSourceId) {
      return true;
    }

    if (
      inputSourceLocationType === 'EXTERNAL' &&
      inputSourceType &&
      (inputSourceId || streamingUrl)
    ) {
      return true;
    }

    return false;
  }, [inputSourceLocationType, inputSourceId, inputSourceType, streamingUrl]);

  const isSettingUp = useCallback(() => setup.srcDev || setup.extType || setup.srcType, [
    setup,
  ]);

  useEffect(() => {
    if (isDeploying || isSettingUp()) return;

    dispatch(fetchNodes());
    dispatch(fetchExternalStreamingDevices());

    return () => {
      dispatch(setSelectedDeployment(null));
      dispatch(setDeviceGroupDeploymentDetails([]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDeploying || isSettingUp()) return;
    let interval: NodeJS.Timeout;

    let deploymentDetailsPromise = dispatch(fetchDeploymentDetails(deploymentId));

    if ((areDeploymentSourcesValid() && status !== 'ERROR') || status === 'DEPLOYING') {
      interval = setInterval(() => {
        deploymentDetailsPromise = dispatch(fetchDeploymentDetails(deploymentId));
      }, REQUEST_POLLING_INTERVAL);
    }

    return () => {
      clearInterval(interval);
      deploymentDetailsPromise.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deploymentId,
    dispatch,
    status,
    modelPurpose,
    isDeploying,
    isSettingUp,
    areDeploymentSourcesValid,
  ]);

  useEffect(() => {
    setSelectedDeploymentSourceConfig(selectedDeployment?.source);
  }, [selectedDeployment]);

  class EdgeStreamObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.REMOTE_STREAM:
          setStreamReady(true);
          break;
        case EVENT_TYPE.CONNECTED:
          setStreamConnected(true);
          break;
        case EVENT_TYPE.CONNECTION_FAILED:
        case EVENT_TYPE.STREAM_FINISHED:
          setStreamConnected(false);
          setStreamReady(false);
          break;
        case EVENT_TYPE.ERROR:
          if (event.message) {
            toast.error(`Streaming error: ${event.message}`);
          } else {
            toast.error(`Streaming error. Please try again.`);
          }
          setStreamConnected(false);
          setStreamReady(false);
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    const controller: VideoStreamController = new VideoStreamController();
    const subject: VideoStreamSubject = new VideoStreamSubject();
    subject.addListener('EdgeStreamObserver', new EdgeStreamObserver());
    if (
      videoOutputHlsUrl &&
      projectId &&
      status === 'RUNNING' &&
      selectedDeployment?.permanent
    ) {
      controller.start({
        limitedDuration: false,
        source: 'IP_CAMERA',
        url: videoOutputHlsUrl,
        streamModel: 'PUSH',
        projectId,
        serverConfig,
        internalSource: true,
        internalNetwork: false,
        monitorConfig: {
          ...DefaultMonitorConfig,
          monitorStream: true,
          projectId,
          deploymentId: storedDeploymentId,
        },
        streamSubject: subject,
      });

      const cleanup = () => {
        controller.stop();
        setStreamReady(false);
      };
      setVideoController(controller);
      window.addEventListener('beforeunload', cleanup);

      return () => {
        window.removeEventListener('beforeunload', cleanup);
        cleanup();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoOutputHlsUrl, serverConfig, status, projectId, storedDeploymentId]);

  const handleDetailsDeployAction = async (values: InputFormValues) => {
    if (status === 'IDLE') {
      if (isDeploying) {
        return;
      }
      setIsDeploying(true);
      if (selectedDeployment) {
        if (!values.inputSource) {
          toast.error('input source is missing');
          setIsDeploying(false);
          return;
        }
        const deploymentStartRequest: StartInferenceRequest = {
          deploymentId: storedDeploymentId!,
          inputSourceType:
            values.inputSourceType === 'DEVICE'
              ? 'DEVICE_CAMERA'
              : values.externalSourceType || null,
          inputSourceId: values.inputSource,
        };

        const result = await dispatch(startDeployment(deploymentStartRequest));
        if (result.meta.requestStatus === 'fulfilled') {
          setExpectedStatus('RUNNING');
        }
      }
      setIsDeploying(false);
    }
  };

  const handleStop = async () => {
    const result = await dispatch(stopDeployment(storedDeploymentId!));
    if (result.meta.requestStatus === 'fulfilled') {
      setExpectedStatus('IDLE');
      setStreamReady(false);
    }
  };

  const initialValues: InputFormValues & DeploymentSettingsAccordionFormValues = {
    inputSourceType: null,
    inputSource: null,
    externalSourceType: null,
    confThreshold: 0.25,
    ddaDataMaxSize: 1000,
    samplingFrequency: 60,
    samplingConfidence: 0,
    nonMaximumSuppression: 0.5,
  };

  if (selectedDeployment && selectedDeploymentSourceConfig) {
    // const source: DeploymentSourceInfo = selectedDeploymentSourceConfig;
    if (selectedDeploymentSourceConfig.inputSourceLocationType === 'DEVICE') {
      initialValues.inputSourceType = 'DEVICE';
      initialValues.inputSource = selectedDeploymentSourceConfig.inputSourceId;
      initialValues.externalSourceType = 'DEVICE_CAMERA';
    } else if (selectedDeploymentSourceConfig.inputSourceLocationType === 'EXTERNAL') {
      initialValues.inputSourceType = 'EXTERNAL';
      if (selectedDeploymentSourceConfig.inputSourceId) {
        initialValues.inputSource = selectedDeploymentSourceConfig.inputSourceId;
      } else if (selectedDeploymentSourceConfig.streamingUrl) {
        initialValues.inputSource =
          externalStreamingDevices.find(
            device => device.url === selectedDeploymentSourceConfig.streamingUrl
          )?.id || null;
      }
      initialValues.externalSourceType = selectedDeploymentSourceConfig.inputSourceType;
    }
  }
  const inputFormSchema = Yup.object().shape({
    inputSourceType: Yup.string()
      .required('Required')
      .typeError('Required'),
    inputSource: Yup.string()
      .required('Required')
      .typeError('Required'),
    externalSourceType: Yup.string()
      .nullable(true)
      .when('inputSourceType', {
        is: 'EXTERNAL',
        then: Yup.string()
          .nullable(false)
          .required('Required')
          .typeError('Required'),
      }),
  });

  const segmentationLegends = useMemo(
    () => selectedDeployment?.deploymentParameters?.segmentationLegend || [],
    [selectedDeployment]
  );

  return (
    <>
      {selectedDeployment ? (
        <Formik
          initialValues={initialValues}
          validationSchema={inputFormSchema}
          validateOnMount
          onSubmit={handleDetailsDeployAction}
          enableReinitialize
        >
          {({values}) => (
            <div className="prod-deploy-details" data-testid="prod-deploy-details">
              <div className="prod-deploy-details__menu">
                {origin === 'dashboard' && (
                  <Button
                    className="prod-deploy-dashboard__back"
                    variant="contained"
                    disableElevation
                    onClick={() => history.push('/dashboard')}
                  >
                    {<FormattedMessage id={'prodDeployment.returnToDashboard'} />}
                  </Button>
                )}
                <Button
                  className="prod-deploy-details__back"
                  variant="contained"
                  disableElevation
                  onClick={() =>
                    history.push(
                      projectContext
                        ? `/project/${projectContext}/deployments`
                        : '/deployments'
                    )
                  }
                >
                  <FormattedMessage id="createDeploy.viewAll" />
                </Button>
              </div>
              <div className="prod-deploy-details__body">
                <div className="prod-deploy-details__info">
                  <div className="prod-deploy-details__header">
                    <h3>{selectedDeployment.name}</h3>
                    <DeploymentButton
                      onStop={handleStop}
                      onStart={() => handleDetailsDeployAction(values)}
                      deployment={selectedDeployment}
                      statusToDisplay={status}
                    />
                  </div>
                  <div className="prod-deploy-details__subheader">
                    {status && <DeployStatus status={status} />}
                    {selectedDeployment.startTime ? (
                      <div>
                        <HumanReadableTime
                          className="prod-deploy-details__time"
                          date={selectedDeployment.startTime}
                        />
                      </div>
                    ) : null}
                    <Button
                      color="primary"
                      variant="contained"
                      disableElevation
                      className="prod-deploy-details__retrain"
                      onClick={() => {
                        dispatch(
                          setRetrainData({
                            projectId,
                            deploymentId: selectedDeployment.id,
                          })
                        );
                        history.push(`/project/${projectId}/retrain`);
                      }}
                      disabled={!selectedDeployment?.retrainAvailable}
                    >
                      <FormattedMessage id="prodDeployment.retrain" />
                    </Button>
                  </div>

                  <StudioTabs
                    className="prod-deploy-details__tabs"
                    value={selectedTab}
                    onChange={(_event, value) => {
                      setSelectedTab(value);
                    }}
                  >
                    <StudioTab
                      disabled={!Boolean(selectedDeployment)}
                      label={intl.formatMessage({id: 'prodDeployment.tabDetails'})}
                      className="prod-deploy-details__tab"
                      icon={<DetailsViewIcon />}
                      data-testid="dplmt-details-details-tab"
                    />
                    <StudioTab
                      disabled={!Boolean(selectedDeployment)}
                      label={intl.formatMessage({id: 'prodDeployment.tabSettings'})}
                      className="prod-deploy-details__tab"
                      icon={<Settings />}
                      data-testid="dplmt-details-settings-tab"
                    />
                  </StudioTabs>

                  <div className="prod-deploy-details__metadata">
                    <StudioTabPanel
                      className="dplmt-tab-panel"
                      value={selectedTab}
                      index={0}
                    >
                      <div className="prod-deploy-details__tab__panel">
                        <DeploymentDetailsMetadata
                          deploymentDetails={selectedDeployment}
                        />
                        <InferenceAppAccordion
                          singlePageApplications={
                            selectedDeployment.singlePageApplications
                          }
                          applicationDetails={selectedDeployment.applicationDetails}
                          projectId={selectedDeployment.projectId}
                          applicationId={selectedDeployment.applicationId}
                          deploymentId={selectedDeployment.id}
                          fromTest={false}
                        />
                      </div>
                    </StudioTabPanel>
                    <StudioTabPanel
                      className="dplmt-tab-panel"
                      value={selectedTab}
                      index={1}
                    >
                      <div className="prod-deploy-details__tab__panel">
                        <DeploymentDetailsSettings
                          deploymentDetails={selectedDeployment}
                          setup={setup}
                          setSetup={setSetup}
                          status={status}
                          nodes={nodes}
                          externalStreamingDevices={externalStreamingDevices}
                        />
                        {/* this would be enabled in future versions not available in 1.5.4 */}
                        {/* <PostProcessorParametersAccordion
                          modelPurpose={selectedDeployment.applicationPurpose}
                        /> */}
                      </div>
                    </StudioTabPanel>
                  </div>
                </div>
                <Card className="prod-deploy-details__results">
                  <h4>{intl.formatMessage({id: 'prodDeployment.inferenceResults'})}</h4>
                  {(streamReady || videoController?.monitor || streamConnected) &&
                  selectedDeployment.status === 'RUNNING' ? (
                    <>
                      <VideoStream
                        className="prod-deploy-details__video"
                        hlsRef={videoController?.hlsClient.hlsRef}
                        streamReady={streamReady}
                        streamConnected={streamConnected}
                        monitor={videoController?.monitor}
                      />
                      <div>
                        <SegmentationLegendView
                          legends={segmentationLegends}
                          placement="row"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="prod-deploy-details__no__results">
                      <img src="images/emptyStateNoResults.png" alt="No deployment" />
                      <FormattedMessage id={'prodDeployment.inferenceAppNoResults'} />
                    </div>
                  )}
                </Card>
              </div>
              <ConfigureDeviceInputsTable
                deviceGroupId={selectedDeployment?.targetDeviceGroupId!}
                deviceGroupName={selectedDeployment?.targetDeviceGroupName!}
                isConfigureDeviceModalOpen={isConfigureDeviceModalOpen}
                setIsConfigureDeviceModalOpen={setIsConfigureDeviceModalOpen}
                isDeploying={isDeploying}
                startUponDeploymentNodes={startUponDeploymentNodes}
                setStartUponDeploymentNodes={setStartUponDeploymentNodes}
                streamingDevices={externalStreamingDevices}
              />
            </div>
          )}
        </Formik>
      ) : (
        <Fade in={!selectedDeployment}>
          <div className="data-prepare__loading">
            <Waiting
              diameter={100}
              loadingText={intl.formatMessage({
                id: 'dataPrep.loading',
              })}
            />
          </div>
        </Fade>
      )}
    </>
  );
};
